/**
 * API client.
 *
 * Thin on purpose: one fetch wrapper that attaches the token, turns the API's
 * structured error envelope into a typed exception, and nothing else. No
 * caching layer — a back-office screen showing a stale member balance is
 * worse than one that takes an extra 80ms.
 *
 * One exception to "and nothing else": a 401 is announced. Clearing the
 * stored token was never enough on its own — see the note on
 * `onUnauthorized` below.
 */

const TOKEN_KEY = 'betterclub.token';

export class ApiError extends Error {
  status: number;
  code: string;
  detail: unknown;

  constructor(status: number, code: string, message: string, detail?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.detail = detail;
  }

  /** True when the ledger refused a write because it would have been wrong. */
  get isInvariant(): boolean {
    return this.status === 422 && this.code.startsWith('invariant_');
  }

  get invariant(): string | null {
    const d = this.detail as { invariant?: string } | null;
    return d?.invariant ?? null;
  }

  get fieldErrors(): { path: string; message: string }[] {
    return Array.isArray(this.detail) ? (this.detail as { path: string; message: string }[]) : [];
  }
}

export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}
export function setToken(token: string | null): void {
  if (token) localStorage.setItem(TOKEN_KEY, token);
  else localStorage.removeItem(TOKEN_KEY);
}

/* ------------------------------------------------------------------ */
/* The token ran out while somebody was using the screen               */
/* ------------------------------------------------------------------ */

/**
 * Everybody who wants to know that the token stopped being accepted.
 *
 * Clearing localStorage on a 401 is not enough and looked like it was.
 * Nothing in React re-reads localStorage, so the session context went on
 * holding a signed-in `me` object, the shell went on rendering, and the
 * screen simply stopped working: every list empty, every tile a dash,
 * every action a red banner. The user is signed out and the only thing
 * that does not know it is the software.
 *
 * So the 401 is announced, once, and the session context turns it into
 * the login screen. Announced rather than redirected from here, because
 * this module has no router and must not have one — the operator console
 * mounts it outside the club session entirely.
 */
type UnauthorizedListener = () => void;
const unauthorizedListeners = new Set<UnauthorizedListener>();

export function onUnauthorized(fn: UnauthorizedListener): () => void {
  unauthorizedListeners.add(fn);
  return () => unauthorizedListeners.delete(fn);
}

/**
 * A screen fires several requests at once, and an expired token fails all
 * of them. Announcing each would sign the user out once per request and,
 * worse, would stack a "your session ended" notice per request on the
 * login card. So the first one wins and the rest are swallowed until
 * somebody signs in again.
 */
let announced = false;

export function armUnauthorized(): void {
  announced = false;
}

function announceUnauthorized(): void {
  if (announced) return;
  announced = true;
  for (const fn of unauthorizedListeners) {
    try {
      fn();
    } catch {
      // A listener that throws must not stop the others being told, and
      // must not turn an expired session into an unhandled rejection in
      // the middle of somebody's fetch.
    }
  }
}

interface RequestOptions {
  method?: string;
  body?: unknown;
  idempotencyKey?: string;
  raw?: boolean;
}

export async function api<T = unknown>(path: string, options: RequestOptions = {}): Promise<T> {
  const headers: Record<string, string> = {};
  const token = getToken();
  if (token) headers.authorization = `Bearer ${token}`;
  if (options.body !== undefined) headers['content-type'] = 'application/json';
  if (options.idempotencyKey) headers['idempotency-key'] = options.idempotencyKey;

  const res = await fetch(path, {
    method: options.method ?? 'GET',
    headers,
    body: options.body === undefined ? undefined : JSON.stringify(options.body),
  });

  if (options.raw) {
    if (!res.ok) {
      // A CSV export is as capable of outliving a token as anything else,
      // and this branch returns before the envelope handling below — so
      // the same two lines have to be here too. They were not, and an
      // export at the wrong moment left the shell up and signed out.
      if (res.status === 401) {
        setToken(null);
        announceUnauthorized();
      }
      throw new ApiError(res.status, 'request_failed', await res.text());
    }
    return (await res.text()) as T;
  }

  const text = await res.text();
  let payload: unknown = null;
  try {
    payload = text ? JSON.parse(text) : null;
  } catch {
    payload = null;
  }

  if (!res.ok) {
    const envelope = payload as { error?: { code: string; message: string; detail?: unknown } };
    if (res.status === 401) {
      // The token is gone or expired. Clear it, then say so out loud — the
      // clearing alone leaves a signed-out user looking at a signed-in
      // shell full of empty tables.
      setToken(null);
      announceUnauthorized();
    }
    throw new ApiError(
      res.status,
      envelope?.error?.code ?? 'unknown',
      envelope?.error?.message ?? `Request failed (${res.status})`,
      envelope?.error?.detail,
    );
  }

  return payload as T;
}

/**
 * An idempotency key for a write the user might resubmit — a double-clicked
 * Post button, a flaky connection, a browser retry. Generated once per form
 * submission attempt, not per render.
 */
export function newIdempotencyKey(prefix: string): string {
  return `${prefix}-${crypto.randomUUID()}`;
}
